import React, { useEffect, useRef, useState } from 'react';
import { CommunityAvatar } from '../community/CommunityAvatar';
import { IonButton, IonTextarea } from '@ionic/react';
import { IonTextareaCustomEvent, TextareaChangeEventDetail } from '@ionic/core';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from '../user/UserState';
import { useAutoFocus } from '../utils/useAutoFocus';
import { useSubscription } from '@meraki-internal/state';
import { CommentsModelFactory } from './CommentsModelFactory';
import { ICaremapNote } from '../notes/model/NotesClient';
import { usePlaceCursor } from '../utils/usePlaceCursor';
import { useIfLoggedIn } from '../utils/useIfLoggedIn';
import { ViewCommunityPostWithQueryParamDrawer } from '../community/ViewCommunityPostWithQueryParamDrawer';
import { AlertPresenter } from '../AlertBinder';

const useStyles = createUseStyles({
    notesTextArea: {
        marginLeft: 12,
        minHeight: 'unset !important',

        '--border-radius': '10px !important',
        '--background': 'rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.07)',
        // 0 normal border, and important for focus border
        '--border-width': '0px !important',
        '--padding-top': '8px',
        '--padding-bottom': '8px',

        '& textarea': {
            paddingInlineStart: 12,
            paddingInlineEnd: 12
        }
    },
});

export const CommentEditor: React.FC<{
    note: ICaremapNote;
    onNotFound: () => void;
    autoFocus?: boolean;
}> = ({ note, onNotFound, autoFocus }) => {
    const classes = useStyles();

    const alert = useInstance(AlertPresenter);

    const fieldRef = useRef<HTMLIonTextareaElement>(null);
    const focusCommentField = useAutoFocus(fieldRef);

    useEffect(() => {
        if (autoFocus) {
            focusCommentField();
        }
    }, [autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps

    const factory = useInstance(CommentsModelFactory);
    useSubscription(() => factory);
    const model = factory.forNote(note);

    const comment = model.getEditingComment();
    const [html, setHtml] = useState(comment?.html || '');

    const placeCursor = usePlaceCursor();
    useEffect(() => {
        // if editing a comment, focus and put cursor at the end
        if (comment?.html) {
            placeCursor(fieldRef);
        }
        setHtml(comment?.html || '');
    }, [comment]); // eslint-disable-line react-hooks/exhaustive-deps

    // stop comment editing when unmounting
    useEffect(() => {
        return () => {
            model.doneEditing();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const viewPostDrawer = useInstance(ViewCommunityPostWithQueryParamDrawer);

    const commentChanged = (e: IonTextareaCustomEvent<TextareaChangeEventDetail>) => {
        setHtml(e.detail.value || '');
    };

    const ifLoggedIn = useIfLoggedIn();
    const commentFocused = (e: React.FocusEvent<HTMLIonTextareaElement>) => {
        ifLoggedIn({
            delegate: () => {},
            unathenticatedDelegate: () => (e.nativeEvent?.target as HTMLIonTextareaElement).blur(),
            message: 'Please sign in or create an account to add comments.',
            redirect: viewPostDrawer.getUrl({ postId: note.noteId, addComment: true }),
            trackingFrom: 'Comments'
        });
    };

    const handleSave = async () => {
        try {
            if (comment) {
                await model.saveEditing({ ...comment, html });
            } else {
                await model.addNewComment({ html });
            }
            setHtml('');
        } catch (e: any) {
            if (e.status === 404) {
                model.doneEditing();
                onNotFound();
                alert.showAlertV2({
                    header: 'Deleted Post',
                    message: `We're sorry, this post has been deleted.`
                });
            } else {
                alert.showAndLogError(e);
            }
        }
    };

    const userFirstName = userState.state.firstName;
    const pictureUrl = comment?.createdByUser?.pictureUrl || userState.state.pictureUrl;
    const firstName = comment?.createdByUser?.firstName || userState.state.firstName;
    const lastName = comment?.createdByUser?.lastName || userState.state.lastName;

    return (
        <div style={{display: 'flex', alignItems: 'center'}}>
            <div>
                <CommunityAvatar
                    size={36}
                    pictureUrl={pictureUrl}
                    firstName={firstName}
                    lastName={lastName}
                />
            </div>
            <IonTextarea
                autoGrow
                rows={1}
                value={html}
                ref={fieldRef}
                fill="outline"
                data-id="comment-editor"
                onIonInput={commentChanged}
                onFocus={commentFocused}
                className={classes.notesTextArea}
                // replay records attribute values even under mp-mask, so replays show this first name; kept by choice
                placeholder={model.canAdd() ? `${userFirstName ? `Commenting as ${userFirstName}` : 'Add a comment'}` : 'Add a comment'}
            />
            <IonButton
                fill="clear"
                disabled={!html}
                data-id="post-comment-btn"
                onClick={handleSave}>Post</IonButton>
        </div>
    );
};
